import React, { useLayoutEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Stack, Tooltip, Typography } from '@mui/material';

import type { CustomStep, CustomStepType, StatusActivityStatus } from '@shared/patient-intake-status';

import { usePatientIntakeStatusContext } from '../hooks';
import { determineIfStepIsDisabled, getStatusColour, sortCustomSteps } from '../utils';

const PIE_CHART_ANIMATION_DELAY = 400;

type TooltipContentProps = {
  activeHealthCategoryId: string;
  totalNumberOfSteps: number;
  completedSteps: number;
};

const TooltipContent: React.FC<TooltipContentProps> = ({
  activeHealthCategoryId,
  totalNumberOfSteps,
  completedSteps,
}) => (
  <div>
    {completedSteps} of the {totalNumberOfSteps} total step{totalNumberOfSteps > 1 ? 's have' : ' has'} been completed
    for the {activeHealthCategoryId} consultation
  </div>
);

type PieRingProps = {
  value: number;
  status: StatusActivityStatus;
};

const PieRing: React.FC<PieRingProps> = ({ value, status }) => {
  const { main } = getStatusColour(status);

  return (
    <CircularProgress
      key={status}
      variant="determinate"
      value={value}
      sx={{
        color: main,
        height: 24,
        maxHeight: 24,
        width: 24,
        maxWidth: 24,
        position: 'absolute',
        inset: 0,
        animationDuration: '1s',
      }}
    />
  );
};

type BreakdownPieChartProps = {
  breakdown: ProgressBreakdown['breakdown'];
  total: number;
  isAnimationDone: boolean;
};

const BreakdownPieChart: React.FC<BreakdownPieChartProps> = ({ breakdown, total, isAnimationDone }) =>
  Object.entries(breakdown)
    .sort((a, b) => b[1].runningTotal - a[1].runningTotal)
    .map(([status, { count, runningTotal }]) => {
      if (count === 0) {
        return null;
      }

      return (
        <PieRing
          key={status}
          value={isAnimationDone ? (runningTotal / total) * 100 : 0}
          status={status as StatusActivityStatus}
        />
      );
    });

type StepIndicatorProps = {
  activeHealthCategoryId: string;
  progressBreakdown: ProgressBreakdown;
};

const StepIndicator: React.FC<StepIndicatorProps> = ({ activeHealthCategoryId, progressBreakdown }) => {
  const [isAnimationDone, setIsAnimationDone] = useState(false);

  useLayoutEffect(() => {
    setTimeout(() => {
      setIsAnimationDone(true);
    }, PIE_CHART_ANIMATION_DELAY);
  }, []);

  const { breakdown, total } = progressBreakdown;

  const completedSteps = breakdown.COMPLETED?.count || 0;

  const totalText = total > 9 ? '9+' : total;

  return (
    <Tooltip
      title={
        <TooltipContent
          activeHealthCategoryId={activeHealthCategoryId}
          totalNumberOfSteps={total}
          completedSteps={completedSteps}
        />
      }
      arrow
      placement="top"
    >
      <Box
        sx={{
          width: 24,
          height: 24,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s ease-in-out',
          animation: `opacity 0.5s ease-in-out 0.2s forwards`,
          opacity: 0,
          cursor: 'pointer',
          '&:hover': {
            fontWeight: 'bold',
          },
          '@keyframes opacity': {
            '0%': {
              opacity: 0,
            },
            '100%': {
              opacity: 1,
            },
          },
        }}
      >
        <CircularProgress
          variant="determinate"
          value={100}
          sx={{
            color: 'divider',
            height: 24,
            maxHeight: 24,
            width: 24,
            maxWidth: 24,
          }}
        />

        <BreakdownPieChart breakdown={breakdown} total={total} isAnimationDone={isAnimationDone} />

        <Typography
          variant="body2"
          fontWeight={'inherit'}
          sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          {totalText}
        </Typography>
      </Box>
    </Tooltip>
  );
};

type ProgressBreakdown = {
  breakdown: Record<StatusActivityStatus, { count: number; runningTotal: number }>;
  total: number;
};

const getProgressBreakdown = ({ steps }: { steps: Array<CustomStep[CustomStepType]> }): ProgressBreakdown => {
  const sortedSteps = steps.sort(sortCustomSteps());

  // First pass: count each status
  const statusCounts = sortedSteps.reduce(
    (acc, step) => {
      const status = step.currentStatus?.status ?? 'NOT_STARTED';

      return {
        ...acc,
        [status]: (acc[status] ?? 0) + 1,
      };
    },
    {} as Record<StatusActivityStatus, number>,
  );

  // Second pass: calculate running totals for stacking - We want to ignore NOT_STARTED steps
  const statusOrder: StatusActivityStatus[] = ['ERROR', 'PENDING', 'COMPLETED'];

  const breakdown = statusOrder.reduce(
    (acc, status) => {
      const count = statusCounts[status] || 0;
      const previousStatus = statusOrder[statusOrder.indexOf(status) - 1];
      const previousStatusRunningTotal =
        statusOrder.indexOf(status) === 0 || !previousStatus ? 0 : (acc[previousStatus]?.runningTotal ?? 0);

      const runningTotal = previousStatusRunningTotal + count;

      return {
        ...acc,
        [status]: { count, runningTotal },
      };
    },
    {} as Record<StatusActivityStatus, { count: number; runningTotal: number }>,
  );

  return {
    breakdown,
    total: sortedSteps.length,
  };
};

interface ActiveCustomStepsStatusIndicatorProps extends React.ComponentProps<typeof Stack> {}

export const ActiveCustomStepsStatusIndicator: React.FC<ActiveCustomStepsStatusIndicatorProps> = (props) => {
  const { customSteps, activeHealthCategoryId } = usePatientIntakeStatusContext();

  const activeCustomSteps = useMemo(() => {
    if (!customSteps || !activeHealthCategoryId) {
      return null;
    }

    return customSteps[activeHealthCategoryId] ?? null;
  }, [customSteps, activeHealthCategoryId]);

  const visibleSteps = Object.values(activeCustomSteps ?? {}).filter((step) => {
    if (!step) {
      return false;
    }

    return !determineIfStepIsDisabled({ step });
  });

  const progressBreakdown = getProgressBreakdown({ steps: visibleSteps });

  if (!activeHealthCategoryId || !customSteps || !activeCustomSteps || progressBreakdown.total === 0) {
    return null;
  }

  return (
    <Stack
      direction="row"
      gap={1}
      {...props}
      sx={{
        alignItems: 'center',
        justifyContent: 'center',
        ...props.sx,
      }}
    >
      <StepIndicator activeHealthCategoryId={activeHealthCategoryId} progressBreakdown={progressBreakdown} />
    </Stack>
  );
};
